import { Skel, SkeletonGroup } from './Skeleton';

/**
 * Skeletons das telas de listagem com as MESMAS classes do conteúdo real
 * (stat-card, sx-table/sx-card, moldura dos redirecionadores) — quando os
 * dados chegam nada muda de lugar.
 */

// Larguras "orgânicas" em px (determinísticas: o skeleton não muda a cada
// render). Px e não %: numa tabela de layout automático, % da célula vira
// barra minúscula; px parece conteúdo de verdade (nome, @bot, valor, data).
const widthFor = (row, col) => 72 + ((row * 37 + col * 53) % 88);

const isBotColumn = (label) => /^bots?$/i.test(String(label).trim());

/** Cartões de métrica (stat-card) — renderize dentro do `.stats-grid` da tela. */
export function StatCardsSkeleton({ count = 4 }) {
    return Array.from({ length: count }, (_, i) => (
        <div key={i} className="stat-card" aria-hidden="true">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-3">
                    <Skel w={`${50 + ((i * 13) % 30)}%`} h={10} />
                    <Skel w={`${40 + ((i * 11) % 25)}%`} h={26} className="rounded-md" />
                    <Skel w="60%" h={9} />
                </div>
                <Skel w={44} h={44} className="rounded-xl shrink-0" />
            </div>
        </div>
    ));
}

/**
 * Tabela (sx-table no desktop, sx-card no mobile). `columns` são os nomes
 * reais dos cabeçalhos: aparecem de verdade, só as células "carregam".
 */
export function TableSkeleton({ label, columns, rows = 10 }) {
    const lines = Array.from({ length: rows }, (_, r) => r);
    return (
        <SkeletonGroup label={label}>
            <div className="sx-desktop">
                <table className="sx-table">
                    <thead>
                        <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                        {lines.map((r) => (
                            <tr key={r}>
                                {columns.map((c, col) => (
                                    <td key={c}>
                                        {isBotColumn(c) ? (
                                            <span className="flex items-center gap-1.5">
                                                <Skel w={28} h={28} circle className="shrink-0" />
                                                <Skel w={widthFor(r, col)} h={11} />
                                            </span>
                                        ) : (
                                            <Skel w={widthFor(r, col)} h={11} />
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="sx-mobile">
                {lines.map((r) => (
                    <div key={r} className="sx-card" aria-hidden="true">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <Skel w={widthFor(r, 0)} h={13} />
                            <Skel w={64} h={20} className="rounded-full shrink-0" />
                        </div>
                        <div className="space-y-2">
                            <Skel w={`${55 + ((r * 13) % 35)}%`} h={10} />
                            <Skel w={`${40 + ((r * 19) % 40)}%`} h={10} />
                        </div>
                    </div>
                ))}
            </div>
        </SkeletonGroup>
    );
}

/**
 * Lista de redirecionadores: mesma moldura e divisórias da RedirectList. O que
 * é marca (caixinha roxa do ícone, chips de bot, botões) usa as classes reais;
 * só o texto fica em shimmer.
 */
export function RedirectListSkeleton({ rows = 5, wideRows = 10 }) {
    // Mesmos cortes da RedirectList: 1280px+ em linha · 1024–1279px empilhado
    // · abaixo de 1024px o bloco de tela menor. Bots um embaixo do outro.
    // Em linha cada item é baixo: a tela grande mostra `wideRows` (a página
    // da lista tem 10); as menores, `rows`.
    const bots = (i, size = 20) => Array.from({ length: 1 + (i % 3) }, (_, b) => (
        <span key={b} className="sx-botchip" aria-hidden="true">
            <Skel w={size} h={size} circle />
            <Skel w={70 + ((i + b) * 17) % 40} h={10} />
        </span>
    ));
    const icon = (
        <div aria-hidden="true"
            className="w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex-shrink-0" />
    );
    return (
        <SkeletonGroup label="Carregando redirecionadores">
            <div className="rounded-2xl border border-white/[0.08] overflow-hidden">
                {Array.from({ length: Math.max(rows, wideRows) }, (_, i) => (
                    <div key={i} data-skel="redirect-row"
                        className={`p-4 lg:px-5 lg:py-4 ${i > 0 ? 'border-t border-white/[0.06]' : ''} ${i >= rows ? 'hidden xl:block' : ''}`}>
                        {/* Desktop */}
                        <div className="hidden lg:flex lg:flex-col xl:flex-row xl:items-center gap-4 xl:gap-5">
                            <div className="flex items-start gap-3 min-w-0 xl:w-[320px] xl:flex-shrink-0">
                                {icon}
                                <div className="min-w-0 flex-1 space-y-2 pt-1">
                                    <Skel w={90 + ((i * 29) % 70)} h={14} />
                                    <Skel w={200 + ((i * 41) % 90)} h={10} style={{ maxWidth: '100%' }} />
                                </div>
                            </div>
                            <div className="min-w-0 xl:flex-1 border-t border-white/[0.06] pt-4 xl:border-t-0 xl:pt-0">
                                <div className="flex flex-col items-start gap-1.5">{bots(i)}</div>
                            </div>
                            <div className="flex items-center gap-2 xl:flex-shrink-0" aria-hidden="true">
                                <span className="btn-secondary btn-action w-2/5 xl:w-auto pointer-events-none justify-center">
                                    <Skel w={56} h={10} />
                                </span>
                                <span className="btn-primary btn-action flex-1 xl:flex-none pointer-events-none justify-center">
                                    <Skel w={120} h={10} style={{ background: 'rgba(255,255,255,0.18)' }} />
                                </span>
                                <span className="btn-secondary btn-action pointer-events-none">
                                    <Skel w={14} h={14} className="rounded" />
                                </span>
                            </div>
                        </div>
                        {/* Tela menor */}
                        <div className="lg:hidden" aria-hidden="true">
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                    {icon}
                                    <div className="min-w-0 flex-1 space-y-2 pt-1">
                                        <Skel w={90 + ((i * 29) % 70)} h={14} />
                                        <Skel w={180 + ((i * 41) % 60)} h={10} style={{ maxWidth: '100%' }} />
                                    </div>
                                </div>
                                <span className="btn-secondary btn-action pointer-events-none">
                                    <Skel w={14} h={14} className="rounded" />
                                </span>
                            </div>
                            <div className="flex flex-col items-start gap-1.5 mb-4">{bots(i)}</div>
                            <div className="flex items-center gap-2">
                                <span className="btn-secondary btn-action flex-1 pointer-events-none justify-center">
                                    <Skel w={56} h={10} />
                                </span>
                                <span className="btn-primary btn-action flex-1 pointer-events-none justify-center">
                                    <Skel w={110} h={10} style={{ background: 'rgba(255,255,255,0.18)' }} />
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </SkeletonGroup>
    );
}
